import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";

const ProjectList = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
  
      if (!token || !user) {
        navigate("/login");
        return;
      }
  
      // 🔎 Trial kontrolü
      const trialActive =
        user.free_trial_end_date &&
        new Date(user.free_trial_end_date) > new Date();
  
      // 🔎 Gerçek erişim kontrolü
      const hasSubscriptionAccess =
        user.isSubscribed ||
        user.lifetimeAccess ||
        trialActive;
  
      // 🔎 Admin + Subscription birlikte olmalı
      if (user.roleId === 1 && hasSubscriptionAccess) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
  
      try {
        const response = await api.get("/api/projects", {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        setProjects(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/login");
        } else if (error.response && error.response.status === 403) {
          // Subscription yoksa burada da yakalayabiliriz
          setIsAdmin(false);
        }
      }
    };
  
    fetchProjects();
  }, [navigate]);
  

  const handleEditClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleSaveChanges = async () => {
    if (!selectedProject) return;

    const token = localStorage.getItem("token");
    try {
      const response = await api.put(
        `/api/projects/${selectedProject.id}`,
        { name: selectedProject.name, description: selectedProject.description },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === selectedProject.id ? response.data : project
        )
      );
      handleCloseModal();
    } catch (error) {
      
    }
  };

  return (
    <Container maxWidth="md">
  <Box className="box-half">
    <Typography variant="h6" className="header">
      <span className="header-text">{t('projects.title')}</span>
      {isAdmin && (
        <Button
          variant="contained"
          className="action-button"
          color="primary"
          onClick={() => navigate("/createproject")}
        >
          {t('projects.createProject')}
        </Button>
      )}
    </Typography>

    {projects.length === 0 && isAdmin ? (
      <Card style={{
        backgroundColor: "#f4f4f4",
        padding: "20px",
        marginTop: "20px",
        textAlign: "center",
        border: "1px solid #6B89D6",
        borderRadius: "15px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      }}>
        <CardContent>
          <Typography variant="h4" gutterBottom style={{ color: "#6B89D6", fontWeight: "bold" }}>
            🌟 {t('projects.welcomeMessage')}
          </Typography>
          <Typography variant="body1" style={{
            marginBottom: "20px",
            color: "#6B89D6",
            fontSize: "1.1rem",
          }}>
            {t('projects.emptyDescription')} 🚀
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              className="action-button"
              color="primary"
              onClick={() => navigate("/createproject")}
            >
              {t('projects.createFirst')}
            </Button>
          )}
        </CardContent>
      </Card>
    ) : (
      <List className="list">
        {projects.map((project) => (
          <ListItem key={project.id} className="list-item">
            <ListItemText
              primary={<span className="list-item-text">{project.name}</span>}
              secondary={<span className="list-item-text">{project.description}</span>}
            />
            {isAdmin && (
              <IconButton className="task-buttons" onClick={() => handleEditClick(project)}>
                <EditIcon />
              </IconButton>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              {t('projects.goToProject')}
            </Button>
          </ListItem>
        ))}
      </List>
    )}
  </Box>

  {/* Edit Project Modal */}
  <Dialog open={isModalOpen} onClose={handleCloseModal}>
    <DialogTitle>{t('projects.editProject')}</DialogTitle>
    <DialogContent>
      <TextField
        fullWidth
        margin="dense"
        label={t('projects.projectName')}
        variant="outlined"
        value={selectedProject?.name || ""}
        onChange={(e) =>
          setSelectedProject({ ...selectedProject, name: e.target.value })
        }
      />
      <TextField
        fullWidth
        margin="dense"
        label={t('projects.description')}
        variant="outlined"
        value={selectedProject?.description || ""}
        onChange={(e) =>
          setSelectedProject({
            ...selectedProject,
            description: e.target.value,
          })
        }
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCloseModal} color="secondary">
        {t('projects.cancel')}
      </Button>
      <Button onClick={handleSaveChanges} color="primary">
        {t('projects.save')}
      </Button>
    </DialogActions>
  </Dialog>
</Container>
  );
};

export default ProjectList;
